import { useMutation, useQuery } from "@apollo/client/react";
import {
  ArrowPathIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { ButtonDots, itemClasses } from "../../../../components/appearance";
import { ErrorFallback } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  GET_FUELENTRY_BY_VEHICLE_ID,
  REMOVE_FUELENTRY,
  VehicleFuelEntry,
} from "../../../../graphql/fleets/vehicles/pages/fuel-entry";
import {
  renderFuelUnit,
  renderPrimaryMeter,
} from "../../../../graphql/fleets/vehicles/pages/settings";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import {
  classNames,
  formatCurrency,
  formatDate,
  formatFloat,
} from "../../../../utils";
import { AuthShield } from "../../../auth/core";

export function FuelEntry({ vehicleId }: { vehicleId: string | undefined }) {
  const { t } = useTranslation();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery<{
    fetchFuelEntriesByVehicle: VehicleFuelEntry[] | null;
  }>(GET_FUELENTRY_BY_VEHICLE_ID, {
    variables: {
      vehicleId,
    },
  });
  const fuelEntries = useMemo<VehicleFuelEntry[]>(() => {
    const updatedFuelEntry = data?.fetchFuelEntriesByVehicle?.length
      ? data?.fetchFuelEntriesByVehicle.map((item) => {
          return {
            ...item,
            fuelEntryDate: formatDate(item.fuelEntryDate),
            createdAt: formatDate(item.createdAt),
          };
        })
      : [];
    return updatedFuelEntry;
  }, [data]);

  const [deleteVehicleFuelEntry] = useMutation(REMOVE_FUELENTRY);
  const handleDelete = async (id: string) => {
    deleteVehicleFuelEntry({
      variables: {
        id: Number(id),
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleFuelEntryDelete) {
          await refetch();
          toast.success(`Vehicle fuel entry deleted successfully`);
        } else {
          toast.error("Something went wrong, please try again later");
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<VehicleFuelEntry, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "fuelEntryDate",
        header: "Fuel Date",
        enableHiding: false,
      },
      {
        accessorKey: "vendor.name",
        header: "Vendor",
        enableHiding: false,
      },
      {
        accessorKey: "odometer",
        header: "Odometer",
        cell: (props) => {
          const odometer = props.row.original.odometer;
          const unit = renderPrimaryMeter(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].primaryMeter
              : null
          );
          return `${odometer} ${unit}`;
        },
      },
      {
        id: "usage",
        header: "Usage",
        cell: (props) => {
          const unit = renderPrimaryMeter(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].primaryMeter
              : null
          );
          const currentOdometer = props.row.original.odometer ?? 0;
          const previousOdometer = props.row.original.previousMeterEntry ?? 0;
          const usage = currentOdometer - previousOdometer;
          return `${usage} ${unit}`;
        },
      },
      {
        accessorKey: "fuelAmount",
        header: "Volume",
        cell: (props) => {
          const fuelAmount = props.row.original.fuelAmount ?? 0;
          const unit = renderFuelUnit(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].fuelUnit
              : null
          );
          return `${fuelAmount} ${unit}`;
        },
      },
      {
        id: "total",
        header: "Total",
        cell: (props) => {
          const fuelAmount = props.row.original.fuelAmount ?? 0;
          const pricePerUnit = props.row.original.pricePerUnit ?? 0;
          const total = formatFloat(fuelAmount * pricePerUnit);
          return formatCurrency(total);
        },
      },
      {
        id: "costPerDistance",
        header: "Cost/Distance",
        cell: (props) => {
          const fuelAmount = props.row.original.fuelAmount ?? 0;
          const pricePerUnit = props.row.original.pricePerUnit ?? 0;
          const total = fuelAmount * pricePerUnit;

          const unit = renderPrimaryMeter(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].primaryMeter
              : null
          );
          const currentOdometer = props.row.original.odometer ?? 0;
          const previousOdometer = props.row.original.previousMeterEntry ?? 0;
          const usage = currentOdometer - previousOdometer;
          const costDistance = formatFloat(total / usage);
          return `${formatCurrency(costDistance)}/${unit}`;
        },
      },
      {
        id: "fuelEconomy",
        header: "Fuel Economy",
        cell: (props) => {
          const fuelAmount = props.row.original.fuelAmount ?? 0;

          const meterUnit = renderPrimaryMeter(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].primaryMeter
              : null
          );
          const fuelUnit = renderFuelUnit(
            props.row.original.vehicle.settings?.length
              ? props.row.original.vehicle.settings[0].fuelUnit
              : null
          );
          const currentOdometer = props.row.original.odometer ?? 0;
          const previousOdometer = props.row.original.previousMeterEntry ?? 0;
          const usage = currentOdometer - previousOdometer;
          const fuelEconomy = formatFloat(usage / fuelAmount);
          return (
            <span className="whitespace-nowrap">{`${fuelEconomy} ${meterUnit}/${fuelUnit}`}</span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <ButtonDots>
            <AuthShield access={["read-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/${vehicleId}/fuel-entry/${props.row.original.id}`}
              >
                <div className="flex items-center">
                  <EyeIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    View
                    <span className="sr-only">
                      , {props.row.original.fuelEntryDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["update-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/${vehicleId}/fuel-entry/${props.row.original.id}/edit`}
              >
                <div className="flex items-center">
                  <PencilIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Edit
                    <span className="sr-only">
                      , {props.row.original.fuelEntryDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                onClick={() => {
                  setAlert({
                    id: props.row.original.id!,
                    name: props.row.original.fuelEntryDate ?? "",
                  });
                }}
              >
                <div className="flex items-center">
                  <TrashIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Delete
                    <span className="sr-only">
                      , {props.row.original.fuelEntryDate}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
          </ButtonDots>
        ),
      },
    ],
    [fuelEntries, vehicleId]
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <Fragment>
      <ResponseAlert />
      <div className="relative">
        <TableCursor
          data={fuelEntries}
          columns={columns}
          loading={loading}
          totalRows={fuelEntries.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          enableRowSelection={false}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No fuel entries found"
          fallbackText="You can add a new fuel entry by clicking on add fuel entry button."
          enableAction
          renderAction={() => (
            <Button
              variant="icon"
              onClick={handleRefetch}
              disabled={loading}
              className="absolute right-0 top-1 flex h-9 items-center justify-center px-2 text-blue-700"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={classNames(
                  "h-5 w-5",
                  refetching ? "animate-spin" : ""
                )}
              />
              <span className="sr-only">Refresh fuel entries list</span>
            </Button>
          )}
        />
      </div>
    </Fragment>
  );
}
